/**
 * iter 110 + 111 — Content Protection primitives.
 *
 * Two concerns behind one Provider, because they share the same 5
 * target routes and the same admin-exemption gate:
 *
 *   • Copy/select/context-menu block (iter 110)
 *   • Diagonal-tiled watermark with viewer identity (iter 111)
 *
 * Both are opt-in via site_settings and can be toggled independently
 * from the admin panel. Admins always exempt (they need to copy AND
 * screenshot for content ops).
 *
 * Public API:
 *   useContentProtection()   → {copyBlockEnabled, watermarkConfig, viewerIdentity}
 *   <ProtectedContent>         wrapper — arms guards + renders watermark overlay
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ContentProtectionContext = createContext({
  copyBlockEnabled: false,
  watermarkConfig: null,
  viewerIdentity: null,
});

export const ContentProtectionProvider = ({ children }) => {
  const { user } = useAuth();
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;

  const [copyBlockEnabled, setCopyBlockEnabled] = useState(false);
  const [watermarkConfig, setWatermarkConfig] = useState(null);
  const [guestIp, setGuestIp] = useState(null);

  // Fetch both flags once per mount. Public endpoints — no auth
  // needed so guests are covered too.
  useEffect(() => {
    let cancelled = false;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    (async () => {
      try {
        const [cp, wm] = await Promise.all([
          axios.get(`${API}/site-settings/content-protection`, { headers }),
          axios.get(`${API}/site-settings/watermark`, { headers }),
        ]);
        if (cancelled) return;
        setCopyBlockEnabled(!!cp.data?.content_protection_enabled);
        setWatermarkConfig({
          enabled: !!wm.data?.watermark_enabled,
          showIndos: !!wm.data?.watermark_show_indos,
          showEmail: !!wm.data?.watermark_show_email,
          showMobile: !!wm.data?.watermark_show_mobile,
          opacity: typeof wm.data?.watermark_opacity === 'number'
            ? Math.max(0, Math.min(1, wm.data.watermark_opacity))
            : 0.08,
          density: typeof wm.data?.watermark_density === 'number'
            ? Math.max(6, Math.min(48, Math.round(wm.data.watermark_density)))
            : 18,
        });
      } catch {
        // Fail open — a settings fetch hiccup shouldn't lock users out.
        if (!cancelled) {
          setCopyBlockEnabled(false);
          setWatermarkConfig({ enabled: false });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, token]);

  // Guests need their IP for the watermark. Only fetch when there's
  // no logged-in user and the watermark is actually enabled — no
  // point wasting a round-trip if the watermark is off.
  useEffect(() => {
    if (user || !watermarkConfig?.enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/site-settings/whoami`);
        if (!cancelled) setGuestIp(res.data?.ip || null);
      } catch {
        // No IP → guest watermark falls back to "HelmTorq" alone.
        if (!cancelled) setGuestIp(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, watermarkConfig?.enabled]);

  // Resolve which lines to show in the watermark for the current viewer.
  const viewerIdentity = useMemo(() => {
    if (!watermarkConfig) return null;
    if (user) {
      const lines = [];
      if (watermarkConfig.showIndos && user.indos_number) lines.push(user.indos_number);
      if (watermarkConfig.showEmail && user.email) lines.push(user.email);
      if (watermarkConfig.showMobile && (user.mobile_number || user.phone)) {
        lines.push(user.mobile_number || user.phone);
      }
      return { role: user.role, lines };
    }
    return { role: 'guest', lines: guestIp ? [guestIp] : [] };
  }, [user, guestIp, watermarkConfig]);

  const value = useMemo(
    () => ({ copyBlockEnabled, watermarkConfig, viewerIdentity }),
    [copyBlockEnabled, watermarkConfig, viewerIdentity],
  );

  return (
    <ContentProtectionContext.Provider value={value}>
      {children}
    </ContentProtectionContext.Provider>
  );
};

export const useContentProtection = () => useContext(ContentProtectionContext);

/**
 * <ContentWatermark> — diagonal-tiled overlay that leaks the viewer's
 * identity into any screenshot. Rendered inside <ProtectedContent> so
 * it only appears on the 5 protected routes. Non-interactive
 * (pointer-events:none) and admin-exempt.
 *
 * Implementation: a full-viewport fixed overlay containing a big grid
 * of rotated text tiles. CSS-only — no canvas, no images. Text uses
 * `attr()` via inline style variables so different pages get their
 * own identity string without extra JS.
 */
const ContentWatermark = () => {
  const { watermarkConfig, viewerIdentity } = useContentProtection();
  const { user } = useAuth();

  if (!watermarkConfig?.enabled) return null;
  if (viewerIdentity?.role === 'admin') return null;

  const lines = ['HelmTorq', ...(viewerIdentity?.lines || [])];
  // Density → grid dimensions. Pick a `cols × rows` factoring close
  // to a 4:3 aspect for desktop; for mobile (portrait, narrow), cap
  // columns at 2-3 and grow rows so tiles don't visually collide.
  // The tile count is preserved either way — deterrent strength stays
  // constant across viewports; only the layout changes. Uses
  // matchMedia so device rotation updates the layout without reload.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches,
  );
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mql = window.matchMedia('(max-width: 767px)');
    const handler = (e) => setIsMobile(e.matches);
    // Safari <14 uses addListener; modern browsers use addEventListener.
    if (mql.addEventListener) mql.addEventListener('change', handler);
    else mql.addListener(handler);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', handler);
      else mql.removeListener(handler);
    };
  }, []);
  const density = watermarkConfig.density ?? 18;
  const desktopLayouts = {
    6: [2, 3],
    12: [3, 4],
    18: [3, 6],
    24: [4, 6],
    30: [5, 6],
    48: [6, 8],
  };
  const mobileLayouts = {
    6: [2, 3],
    12: [2, 6],
    18: [2, 9],
    24: [3, 8],
    30: [3, 10],
    48: [3, 16],
  };
  const layoutMap = isMobile ? mobileLayouts : desktopLayouts;
  // Round to nearest layout step. Small map keeps behaviour predictable
  // vs. computing dims from arbitrary integers.
  const stops = Object.keys(layoutMap).map(Number).sort((a, b) => a - b);
  const nearest = stops.reduce((prev, curr) =>
    Math.abs(curr - density) < Math.abs(prev - density) ? curr : prev,
  );
  const [cols, rows] = layoutMap[nearest];
  const totalTiles = cols * rows;
  const repeats = Array.from({ length: totalTiles });
  const opacity = watermarkConfig.opacity ?? 0.08;

  return (
    <div
      aria-hidden="true"
      data-testid="content-watermark-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
        zIndex: 40, // above content, below dialogs (z-50) & toasts
        // Grid of tiles across the viewport, density-controlled.
        display: 'grid',
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        userSelect: 'none',
      }}
    >
      {repeats.map((_, i) => (
        <div
          key={i}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transform: 'rotate(-30deg)',
            opacity,
            color: '#000',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            fontSize: 13,
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1.3,
            whiteSpace: 'nowrap',
          }}
        >
          <div>
            {lines.map((ln, idx) => (
              <div key={idx}>{ln}</div>
            ))}
          </div>
        </div>
      ))}
      {/* Debug marker to help find the wm in dev-tools inspection. */}
      <span style={{ display: 'none' }} data-user-role={user?.role || 'guest'} />
    </div>
  );
};

/**
 * <ProtectedContent> — wraps any block of proprietary content. Arms:
 *   • Copy/cut/context/selectstart/dragstart blockers (iter 110)
 *   • CSS user-select: none
 *   • Overlaid <ContentWatermark> (iter 111)
 *
 * Both concerns respect their own toggle in site_settings and the
 * shared admin-exemption. Result: admins see plain content, everyone
 * else sees whatever protection the admin has enabled.
 */
export const ProtectedContent = ({
  as: Tag = 'div',
  className,
  children,
  style,
  ...rest
}) => {
  const { copyBlockEnabled } = useContentProtection();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const armed = copyBlockEnabled && !isAdmin;

  const block = useCallback(
    (e) => {
      if (armed) e.preventDefault();
    },
    [armed],
  );

  const combinedStyle = armed
    ? {
        userSelect: 'none',
        WebkitUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
        WebkitTouchCallout: 'none',
        ...style,
      }
    : style;

  return (
    <Tag
      className={className}
      style={combinedStyle}
      onCopy={block}
      onCut={block}
      onContextMenu={block}
      onSelectStart={block}
      onDragStart={block}
      {...rest}
    >
      {children}
      <ContentWatermark />
    </Tag>
  );
};

export default ProtectedContent;
